import { useEffect } from "react";

// Keeps the screen on (no dimming, no sleep) while `on`: a game is on screen,
// played or watched. The Screen Wake Lock API (Chrome, so the Android app too;
// Safari 16.4+): the browser lets go of the lock whenever the page is hidden,
// so it is asked for again each time the page comes back. Where it is missing
// or refused (no user gesture yet, battery saver), nothing happens.
type Sentinel = { release: () => Promise<void>; addEventListener: (t: string, f: () => void) => void };

export function useWakeLock(on = true): void {
  useEffect(() => {
    const wl = (navigator as any).wakeLock as { request: (type: "screen") => Promise<Sentinel> } | undefined;
    if (!on || !wl) return;
    let lock: Sentinel | null = null;
    let alive = true;
    const take = async () => {
      if (!alive || lock || document.visibilityState !== "visible") return;
      try {
        const got = await wl.request("screen");
        if (!alive) { void got.release().catch(() => {}); return; }
        lock = got;
        got.addEventListener("release", () => { if (lock === got) lock = null; });
      } catch { /* refused: the screen may dim as usual */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") void take(); };
    void take();
    document.addEventListener("visibilitychange", onVisible);
    // (a first tap or key counts as the gesture some browsers want before granting it)
    document.addEventListener("pointerdown", onVisible);
    document.addEventListener("keydown", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("pointerdown", onVisible);
      document.removeEventListener("keydown", onVisible);
      if (lock) void lock.release().catch(() => {});
      lock = null;
    };
  }, [on]);
}
